import { type GameFormGroupParticipant } from '../form'
import {
  type UserId,
  type GroupId,
  type DecoratedGameGroup,
  isNotDefined,
  splitArray,
  isDefined,
  isNotEmpty,
} from '@terros/common'

type AddRemoveGroupParticipants = {
  usersToRemove: UserId[]
  groupsToAdd: GameFormGroupParticipant[]
  groupsToUpdate: GameFormGroupParticipant[]
  groupsToRemove: GroupId[]
}

export const toAddRemoveGroupParticipants = (
  newGroups: GameFormGroupParticipant[],
  oldGroups: DecoratedGameGroup[]
): AddRemoveGroupParticipants => {
  const allNewUsers = newGroups.flatMap((g) => g.userIds)
  const allOldUsers = oldGroups.flatMap((g) => g.users.map((u) => u.participantId))

  const usersToRemove = allOldUsers.filter((u) => {
    // remove user if not in new groups
    if (!allNewUsers.includes(u)) return true
    // remove user if group user has changed, user will be re-added when add group
    const oldGroup = oldGroups.find((g) => g.users.some((user) => user.participantId === u))
    if (!oldGroup) return false
    const newGroup = newGroups.find((g) => g.userIds.includes(u))
    if (!newGroup) return false
    return oldGroup.participantId !== newGroup.participantId
  })

  const { yes: rest, no: toRemove } = splitArray(oldGroups, (g) =>
    newGroups.some((ng) => ng.participantId === g.participantId)
  )

  const groupsToRemove = toRemove.map((g) => g.participantId)

  const groupsToAdd = newGroups
    .map((g) => {
      // add new group wiht not groupId
      if (isNotDefined(g.participantId)) return g
      // add existing group with changed users list
      const oldGroup = rest.find((group) => g.participantId === group.participantId)
      if (isNotDefined(oldGroup)) return
      if (hasGroupUsersChanged(g, oldGroup)) {
        const newUserIds = getNewGroupUsers(
          g.userIds,
          oldGroup.users.map((user) => user.participantId)
        )
        if (isNotEmpty(newUserIds)) {
          return {
            ...g,
            userIds: newUserIds,
          }
        }
      }
    })
    .filter(isDefined)
    .filter((g) => isNotEmpty(g.userIds))

  const groupsToUpdate = newGroups.filter((g) => {
    if (isNotDefined(g.participantId)) return false
    const oldGroup = rest.find((group) => g.participantId === group.participantId)
    if (isNotDefined(oldGroup)) return false
    return g.name !== oldGroup.name
  })

  return {
    usersToRemove,
    groupsToAdd,
    groupsToRemove,
    groupsToUpdate,
  }
}

const hasGroupUsersChanged = (newGroup: GameFormGroupParticipant, oldGroup: DecoratedGameGroup): boolean => {
  if (oldGroup.users.length !== newGroup.userIds.length) return true
  const oldUserIds = oldGroup.users.map((user) => user.participantId)
  const newUserIds = newGroup.userIds
  const hasNewUser = newUserIds.some((id) => !oldUserIds.includes(id))
  return hasNewUser
}

const getNewGroupUsers = (newUserIds: UserId[], oldUserIds: UserId[]): UserId[] => {
  return newUserIds.filter((newUserId) => !oldUserIds.includes(newUserId))
}
